<form class="production-report-viewer__settings" (submit)="onSubmit($event)">
  <!-- The fields scroll on their own, the way the app's modals do; the actions stay below them. -->
  <dx-scroll-view
    class="production-report-viewer__settings-scroll"
    direction="vertical"
    [useNative]="false"
    [showScrollbar]="'always'"
  >
    <div class="production-report-viewer__settings-fields">
      <section class="report-settings-section">
        <button
          type="button"
          class="report-settings-section__header"
          [attr.aria-expanded]="layoutOpen"
          aria-controls="report-layout-fields"
          (click)="toggleSection('layout')"
        >
          <i
            class="dx-icon"
            [class.dx-icon-chevrondown]="layoutOpen"
            [class.dx-icon-chevronright]="!layoutOpen"
            aria-hidden="true"
          ></i>
          <strong>Layout</strong><span [title]="layoutSummary">{{ layoutSummary }}</span>
        </button>
        <div id="report-layout-fields" [hidden]="!layoutOpen">
          <dx-form
            #settingsFormRef
            [formData]="formData"
            [colCount]="1"
            labelMode="floating"
            [showColonAfterLabel]="false"
          >
            <dxi-item
              dataField="title"
              editorType="dxTextBox"
              [label]="{ text: 'Bulletin Title' }"
              [editorOptions]="titleEditorOptions"
            ></dxi-item>
            <dxi-item
              dataField="subtitle"
              editorType="dxTextBox"
              [label]="{ text: 'Report Title' }"
              [editorOptions]="subtitleEditorOptions"
            ></dxi-item>
            <dxi-item
              dataField="brandingId"
              editorType="dxSelectBox"
              [visible]="showBranding"
              [label]="{ text: 'Branding' }"
              [editorOptions]="brandingIdEditorOptions"
            >
              <dxi-validation-rule type="required" message="Branding is required"></dxi-validation-rule>
            </dxi-item>
            <dxi-item
              dataField="rowsPerPage"
              editorType="dxNumberBox"
              [label]="{ text: 'Rows Per Page' }"
              [editorOptions]="rowsPerPageEditorOptions"
            ></dxi-item>
          </dx-form>
        </div>
      </section>
      @if (periodSelection; as selection) {
        <section class="report-settings-section">
          <button
            type="button"
            class="report-settings-section__header"
            [attr.aria-expanded]="periodOpen"
            aria-controls="report-period-fields"
            (click)="toggleSection('period')"
          >
            <i
              class="dx-icon"
              [class.dx-icon-chevrondown]="periodOpen"
              [class.dx-icon-chevronright]="!periodOpen"
              aria-hidden="true"
            ></i>
            <strong>Period</strong><span [title]="periodSummary">{{ periodSummary }}</span>
          </button>
          <div id="report-period-fields" [hidden]="!periodOpen">
            <ag-shr-report-period-control
              [showLabel]="false"
              [selection]="selection"
              [paid]="paidReport"
              [through]="dataAvailableThrough"
              [from]="dataAvailableFrom"
              (selectionChange)="periodSelectionChange.emit($event)"
            ></ag-shr-report-period-control>
          </div>
        </section>
      }
      @if (variableFieldConfigsSig().length) {
        <section class="report-settings-section">
          <button
            type="button"
            class="report-settings-section__header"
            [attr.aria-expanded]="displayOpen"
            aria-controls="report-display-fields"
            (click)="toggleSection('display')"
          >
            <i
              class="dx-icon"
              [class.dx-icon-chevrondown]="displayOpen"
              [class.dx-icon-chevronright]="!displayOpen"
              aria-hidden="true"
            ></i>
            <strong>Display &amp; NTP</strong><span [title]="displaySummary">{{ displaySummary }}</span>
          </button>
          <div id="report-display-fields" [hidden]="!displayOpen">
            <dx-form
              #settingsFormRef
              class="production-report-viewer__settings-form"
              [formData]="formData"
              [colCount]="1"
              labelMode="floating"
              [showColonAfterLabel]="false"
              [showValidationSummary]="true"
            >
              @if (variableFieldConfigsSig().length) {
                <dxi-item itemType="group" [caption]="''" cssClass="production-report-viewer__settings-variables">
                  @for (fieldConfig of variableFieldConfigsSig(); track fieldConfig.variable.name) {
                    @if (
                      fieldConfig.variable.name === ReportInputParameterKeys.displayThreshold &&
                        includeThresholdFieldConfigSig();
                      as switchConfig
                    ) {
                      <!-- The switch shares the threshold's row: it decides whether that amount filters at all. -->
                      <dxi-item itemType="group" [colCount]="6" [colCountByScreen]="thresholdRowColCountByScreen">
                        <dxi-item
                          [dataField]="fieldConfig.dataField"
                          [editorType]="fieldConfig.editorType"
                          [colSpan]="5"
                          [label]="{ text: fieldConfig.variable.label }"
                          [editorOptions]="thresholdEditorOptionsSig()"
                        ></dxi-item>
                        <dxi-item
                          [dataField]="switchConfig.dataField"
                          [editorType]="switchConfig.editorType"
                          [label]="{ text: switchConfig.variable.label, visible: false }"
                          [editorOptions]="switchConfig.editorOptions"
                          [cssClass]="switchConfig.cssClass"
                        ></dxi-item>
                      </dxi-item>
                    } @else {
                      <dxi-item
                        [dataField]="fieldConfig.dataField"
                        [editorType]="fieldConfig.editorType"
                        [isRequired]="fieldConfig.variable.required"
                        [label]="
                          fieldConfig.variable.name === ReportInputParameterKeys.ntpDisplayMode
                            ? { text: fieldConfig.variable.label, template: 'reportVariableLabel', visible: true, location: 'top' }
                            : fieldConfig.helpText
                              ? { text: fieldConfig.variable.label, template: 'reportVariableLabel' }
                              : { text: fieldConfig.variable.label }
                        "
                        [editorOptions]="fieldConfig.editorOptions"
                        [cssClass]="fieldConfig.cssClass"
                      >
                        @if (fieldConfig.variable.required) {
                          <dxi-validation-rule
                            type="required"
                            [message]="fieldConfig.variable.label + ' is required'"
                          ></dxi-validation-rule>
                        }
                      </dxi-item>
                    }
                  }
                </dxi-item>
              }
        <div *dxTemplate="let labelData of 'reportVariableLabel'" class="production-report-viewer__settings-label">
          <span>{{ labelData.text }}</span>
          <span
            #helpIcon
            class="production-report-viewer__settings-help-icon"
            tabindex="0"
            role="img"
            aria-label="Help"
          >
            ?
            <dx-tooltip
              [target]="helpIcon"
              showEvent="mouseenter focusin"
              hideEvent="mouseleave focusout"
              [position]="'top'"
              [width]="360"
            >
              <div class="production-report-viewer__settings-help-tooltip" *dxTemplate="let tooltipData of 'content'">
                {{ getVariableHelpText(labelData.text) }}
              </div>
            </dx-tooltip>
          </span>
        </div>
            </dx-form>
          </div>
        </section>
      }
    </div>
  </dx-scroll-view>

  <!-- Outside the scroll view, so Apply is always in reach. -->
  <div class="production-report-viewer__settings-actions">
    <dx-button
      text="Reset to Defaults"
      type="normal"
      stylingMode="text"
      [disabled]="isLoading"
      (onClick)="resetToDefaults.emit()"
    ></dx-button>
    <dx-button
      [text]="isLoading ? 'Applying...' : 'Apply & Rerun'"
      type="default"
      stylingMode="contained"
      [useSubmitBehavior]="true"
      [disabled]="isLoading || periodInvalid"
    ></dx-button>
  </div>
</form>
